(() => {
  const SELECTOR = '[data-wave-marquee]';

  class WaveMarqueeAnimator {
    constructor(el) {
      this.el = el;
      this.textPath = el.querySelector('.wave-marquee__textPath');
      this.path = el.querySelector('.wave-marquee__path');

      if (!this.textPath || !this.path) return;

      this.speed = parseFloat(el.dataset.speed) || 25; // seconds per loop
      this.direction = el.dataset.direction === 'right' ? -1 : 1;
      this.pauseOnHover = el.dataset.pauseOnHover === 'true';
      this.offset = parseFloat(el.dataset.startOffset) || 0;

      this.lastTs = null;
      this.raf = null;
      this.running = false;
      this.visible = true;

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.tick = this.tick.bind(this);
      this.onReduceMotionChange = this.onReduceMotionChange.bind(this);

      // Reduced motion: don't animate
      this.reduceMotion.addEventListener?.('change', this.onReduceMotionChange);

      // Pause when offscreen (saves CPU)
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.target !== this.el) continue;
              this.visible = entry.isIntersecting;
              this.updateRunningState();
            }
          },
          { threshold: 0.01 }
        );
        this.io.observe(this.el);
      }

      // Optional pause on hover
      if (this.pauseOnHover) {
        this.el.addEventListener('mouseenter', () => this.stop(), { passive: true });
        this.el.addEventListener('mouseleave', () => this.start(), { passive: true });
      }

      // Start
      this.updateRunningState(true);
    }

    onReduceMotionChange() {
      this.updateRunningState();
    }

    updateRunningState(forceStart = false) {
      if (this.reduceMotion.matches) {
        this.stop();
        return;
      }
      if (!this.visible && !forceStart) {
        this.stop();
        return;
      }
      this.start();
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.lastTs = null;
      this.raf = requestAnimationFrame(this.tick);
    }

    stop() {
      if (!this.running) return;
      this.running = false;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      this.lastTs = null;
    }

    tick(ts) {
      if (!this.running) return;

      if (this.lastTs == null) this.lastTs = ts;
      const dt = (ts - this.lastTs) / 1000;
      this.lastTs = ts;

      const pctPerSec = 100 / Math.max(this.speed, 1);
      this.offset = (this.offset + dt * pctPerSec * this.direction) % 100;
      if (this.offset < 0) this.offset += 100;

      this.textPath.setAttribute('startOffset', `${this.offset}%`);

      this.raf = requestAnimationFrame(this.tick);
    }

    destroy() {
      this.stop();
      this.io?.disconnect();
      this.reduceMotion.removeEventListener?.('change', this.onReduceMotionChange);
      delete this.el.__waveMarqueeAnimator;
    }
  }

  function init(root = document) {
    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (el.__waveMarqueeAnimator) return;
      el.__waveMarqueeAnimator = new WaveMarqueeAnimator(el);
    });
  }

  // Initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme Editor live reload events
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll(SELECTOR).forEach((el) => el.__waveMarqueeAnimator?.destroy?.());
  });
})();
